<template>
  <div id="kn-builder">
    <div id="modal-1" />
    <div id="modal-2" />
    <ChecklistHighlightObserver />
    <ToastNotifications />
    <GlobalNotifications />
    <AssetViewer v-if="assetIsLoaded" />
    <OverlayBackground
      id="kn-login-overlay-background"
      style="display: none"
    >
      <Login ref="reauthenticationLogin" />
    </OverlayBackground>
    <template v-if="appIsLoaded || !$route.meta.requiresAuth">
      <RouterView />
    </template>
    <template v-else>
      <GlobalLoading :is-loading="isLoadingRequest" />
    </template>
    <SvgGradients />
    <IntercomLauncher />
  </div>
</template>

<script>
import LogRocket from 'logrocket';
import { mapGetters } from 'vuex';
import { generateCssVariables } from '@knack/asterisk-react';
import AssetViewer from '@/components/renderer/shared/AssetViewer';
import GlobalLoading from '@/components/ui/GlobalLoading';
import GlobalNotifications from '@/components/ui/notifications/GlobalNotifications';
import Login from '@/components/user/Login';
import OverlayBackground from '@/components/ui/OverlayBackground';
import { shouldEnableLogRocket } from '@/lib/env-helper';
import { safeDatadogRumAddError } from '@/lib/datadog-rum-helper';
import SvgGradients from '@/components/ui/SvgGradients';
import ChecklistHighlightObserver from '@/components/checklists/ChecklistObservers.vue';
import ToastNotifications from '@/components/ui/notifications/ToastNotifications.vue';
import store from '@/store';
import { getCurrentValidObject } from '@/lib/intercom-helper';
import bootIntercom from './components/intercom/intercom';
import IntercomLauncher from '@/components/intercom/IntercomLauncher.vue';

export default {
  components: {
    ToastNotifications,
    ChecklistHighlightObserver,
    AssetViewer,
    GlobalLoading,
    GlobalNotifications,
    Login,
    IntercomLauncher,
    OverlayBackground,
    SvgGradients,
  },
  computed: {
    ...mapGetters([
      'appIsLoaded',
    ]),
    ...mapGetters('assets', [
      'assetIsLoaded',
    ]),
  },
  watch: {
    async appIsLoaded(newVal) {
      if (newVal && process.env.VUE_APP_INTERCOM_APP_ID) {
        const {
          email, first_name, last_name, user_id,
        } = window.Knack.Api;
        const firstObject = getCurrentValidObject().attributes.key;
        const firstScene = store.getters.allPages[0]?.raw.key;
        log('first', firstObject, firstScene);
        await bootIntercom({
          email,
          name: `${first_name} ${last_name}`,
          user_id,
          account: store.getters.app.account.slug,
          app_slug: store.getters.app.slug,
          builder_version: '3',
          current_valid_object: firstObject,
          current_valid_scene: firstScene,
        });
      }
    },
  },
  mounted() {
    this.insertThemeStyles();
    document.documentElement.dataset.theme = 'light';
  },
  errorCaptured(err) {
    if (shouldEnableLogRocket()) {
      LogRocket.captureException(err);
    }
    safeDatadogRumAddError(err, {
      source: 'vue-error-boundary',
      component: 'App',
    }, this.$store.getters.app);
  },
  methods: {
    insertThemeStyles() {
      const id = 'theme-styles';
      if (!document.getElementById(id)) {
        document.head.insertAdjacentHTML(
          'beforeend',
          `<style id="${id}">${generateCssVariables()}</style>`,
        );
      }
    },
    async hideIntercom() {
      // hide intercom container if it exists
      const intercomContainer = document.getElementById('intercom-container');
      if (intercomContainer) {
        intercomContainer.style.display = 'none';
      }

      // use Intercom JS API to hide the launcher and any open windows
      window.Intercom('update', {
        hide_default_launcher: true,
      });
      window.Intercom('hide');

      // in some cases intercom may reopen after we have
      // already closed it, so we need to hide it again
      setTimeout(() => {
        window.Intercom('update', {
          hide_default_launcher: true,
        });
        window.Intercom('hide');
      }, 2000);
    },
  },
};
</script>
